import { Component, OnDestroy, OnInit } from '@angular/core';
import { ImmoIPPlacerImmoTaskDetail } from './helper/model/immo-ip-placer-immo-task-detail';
import { User } from 'src/app/model/user';
import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';
import { ActivatedRoute, Params, Router, UrlSegment } from '@angular/router';
import {
  ImmoTaskChangeType,
  ImmoTaskWithChangeType,
  OfflineStorageTourService,
} from '../../tour-view/offline-storage/offline-storage-tour.service';
import { ImmoIPImmoTaskChangesService } from '../../../../../services/immo-ip-placer-offline-changes/immo-ip-immo-task-changes/immo-ip-immo-task-changes.service';
import { AuthService } from 'src/app/services/auth.service';
import { SnackService } from 'src/app/services/snack.service';
import { Lightbox } from 'ngx-lightbox';
import { combineLatest } from 'rxjs';
import { take } from 'rxjs/operators';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import UserHelper from 'src/app/helpers/user-helper';
import { StringHelper } from 'src/app/helpers/string-helper';
import DateHelper from 'src/app/helpers/date-helper';
import {
  getValueFromResolvedOrPromise,
  KeysOfType,
  ResolvedOrPromise,
} from 'src/app/helpers/utility-functions';
import Asset from 'src/app/model/asset';
import { ImmoTask } from 'src/app/model/immo-task/immo-task';
import { ImmoIPPlacerImmoTaskChangeStorageHandler } from './helper/change-storage-handler/immo-ip-placer-immo-task-change-storage-handler';
import { ImmoIPPlacerImmoTaskChangesFunctions } from './helper/change-storage-handler/immo-ip-placer-immo-task-change-functions';
import { ImmoIPPlacerImmoTaskDetailHelper } from './helper/immo-ip-placer-immo-task-detail-helper';
import { ImmoIPPlacerImmoTaskChangeStorageHandlerHelper } from './helper/change-storage-handler/immo-ip-placer-immo-task-change-storage-handler-helper';
import { ImmoIPImmoTaskSubmitService } from 'src/app/services/immo-ip-immo-task-submit/immo-ip-immo-task-submit.service';
import ErrorHelper from 'src/app/helpers/error-helper';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import { ImmoIPImmoTaskSubmitQueueService } from 'src/app/services/immo-ip-immo-task-submit/immo-ip-immo-task-submit-queue.service';
import { PlanningDateService } from 'src/app/services/planning-date-service';
import { ImmoIPPlacerTourViewHelper } from '../../tour-view/helper/immo-ip-placer-tour-view-helper';
import { CompletedAssignmentModalComponent } from '../../../../../shared/completed-assignment-modal/completed-assignment-modal.component';
import {
  ModalService,
  ObservableHandlerPair,
} from 'src/app/services/modal.service';

type AssetsProp = KeysOfType<ImmoIPPlacerImmoTaskDetailComponent, Asset[]>;

@Component({
  selector: 'immo-ip-placer-immo-task-detail',
  templateUrl: './immo-ip-placer-immo-task-detail.component.html',
  styleUrls: ['./immo-ip-placer-immo-task-detail.component.scss'],
})
export class ImmoIPPlacerImmoTaskDetailComponent
  extends SubscriptionBasedComponent
  implements OnInit, OnDestroy
{
  private static readonly HANDLE_CHANGED_DEBOUNCE = 500;

  init: boolean = false;

  reloading: boolean = false;

  user: User;
  placerName: string;
  planningDate: string;

  immoTask: ImmoIPPlacerImmoTaskDetail;

  designAssets: Asset[];
  locationAssets: Asset[];
  executionAssets: Asset[];

  private date: Date;

  private immoIPPlacerImmoTaskChangeStorageHandler: ImmoIPPlacerImmoTaskChangeStorageHandler;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private offlineStorageTourService: OfflineStorageTourService,
    private immoIPImmoTaskChangesService: ImmoIPImmoTaskChangesService,
    private planningDateService: PlanningDateService,
    private authService: AuthService,
    private snackService: SnackService,
    private modalService: ModalService,
    private immoIPImmoTaskSubmitService: ImmoIPImmoTaskSubmitService,
    private immoIPImmoTaskSubmitQueueService: ImmoIPImmoTaskSubmitQueueService,
    private _lightbox: Lightbox
  ) {
    super();
  }

  ngOnInit(): void {
    this.initializeSubscriptions();
  }

  private initializeSubscriptions(): void {
    const routeObservable = combineLatest([
      this.route.params,
      this.route.url,
    ]).pipe(take(1));
    this.addSubscription(
      routeObservable,
      ([params, urlSegments]: [Params, UrlSegment[]]) =>
        this.handleLoadImmoTask(params, urlSegments)
    );

    this.addSubscription(
      this.offlineStorageTourService.getImmoTaskHasChangedObservable(),
      async (immoTaskWithChangeType: ImmoTaskWithChangeType) => {
        if (
          immoTaskWithChangeType.immoTask != null &&
          this.immoTask.id === immoTaskWithChangeType.immoTask.id
        ) {
          this.reloading = true;
          try {
            await ImmoIPPlacerImmoTaskDetailHelper.handleImmoTaskChanged(
              this.immoTask,
              immoTaskWithChangeType.immoTask,
              immoTaskWithChangeType.changeType,
              this.immoIPImmoTaskChangesService,
              this.immoIPPlacerImmoTaskChangeStorageHandler
            );
            if (
              immoTaskWithChangeType.changeType === ImmoTaskChangeType.ASSETS
            ) {
              this.executionAssets =
                ImmoIPPlacerImmoTaskChangeStorageHandlerHelper.getExecutionAssets(
                  this.immoTask,
                  this.immoIPPlacerImmoTaskChangeStorageHandler
                );
            }
          } finally {
            this.reloading = false;
          }
        }
      },
      GeneralHelper.noop,
      ImmoIPPlacerImmoTaskDetailComponent.HANDLE_CHANGED_DEBOUNCE
    );

    this.addSubscription(
      this.planningDateService.getSelectedPlanningDateSubscribable(),
      (date: Date) => {
        this.date = date;
      }
    );
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.immoIPPlacerImmoTaskChangeStorageHandler != null) {
      this.immoIPPlacerImmoTaskChangeStorageHandler.destroy();
      this.immoIPPlacerImmoTaskChangeStorageHandler = null;
    }
  }

  private handleLoadImmoTask(params: Params, urlSegments: UrlSegment[]): void {
    if (params == null || !CollectionHelper.isPopulated(urlSegments)) {
      throw new Error('Ongeldige route');
    }
    this.handleLoadOwnImmoTask(params);
    // add logic to load task from other tour if such functionality is ever requested
  }

  private handleLoadOwnImmoTask(params: Params): void {
    this.initializePlacer();
    const dateString =
      params['dateString'] == null ? null : params['dateString'];
    if (StringHelper.isEmpty(dateString)) {
      this.snackService.showError('dateString ontbreekt');
      return;
    }
    const date = DateHelper.parseDate(dateString);
    this.planningDate = DateHelper.formatDate(date, 'dd/MM/yyyy');
    const immoTaskId =
      params['immoTaskId'] == null ? null : Number(params['immoTaskId']);
    if (immoTaskId == null) {
      this.snackService.showError('immoTaskId ontbreekt');
      return;
    }
    this.doLoadImmoTask(
      {
        promise: this.offlineStorageTourService.getImmoTask(
          dateString,
          immoTaskId
        ),
      },
      dateString
    );
  }

  private async initializePlacer(): Promise<void> {
    this.user = await this.authService
      .waitUntilAuthenticatedUserLoadedSubscribable()
      .toPromise();
    this.placerName = UserHelper.getDisplayName(this.user);
  }

  private async doLoadImmoTask(
    loadImmoTaskResolvedOrPromise: ResolvedOrPromise<ImmoTask>,
    dateString: string
  ): Promise<void> {
    try {
      this.immoTask = await getValueFromResolvedOrPromise(
        loadImmoTaskResolvedOrPromise
      );
      [this.designAssets, this.locationAssets] =
        ImmoIPPlacerImmoTaskDetailHelper.processAssets(this.immoTask);
      this.immoIPPlacerImmoTaskChangeStorageHandler =
        await ImmoIPPlacerImmoTaskChangeStorageHandlerHelper.initializeImmoIPPlacerImmoTaskChangeStorageHandler(
          this.immoTask,
          dateString,
          this.immoIPImmoTaskChangesService,
          this.immoIPImmoTaskSubmitService,
          this.snackService
        );
      ImmoIPPlacerImmoTaskDetailHelper.fillImmoTaskToDoItems(
        this.immoTask,
        this.immoIPPlacerImmoTaskChangeStorageHandler
      );
      this.executionAssets =
        ImmoIPPlacerImmoTaskChangeStorageHandlerHelper.getExecutionAssets(
          this.immoTask,
          this.immoIPPlacerImmoTaskChangeStorageHandler
        );
    } finally {
      this.init = true;
    }
  }

  openLightbox(prop: AssetsProp): void {
    if (prop == null || !CollectionHelper.isPopulated(this[prop])) {
      return;
    }

    const album = this[prop].map((a) => {
      return {
        src: `/api/assets/${a.uuid}`,
        thumb: `/api/assets/${a.uuid}`,
      };
    });
    this._lightbox.open(album);
  }

  startExecution(): void {
    this.immoIPPlacerImmoTaskChangeStorageHandler.storeOfflineTaskChange(
      true,
      ImmoIPPlacerImmoTaskChangesFunctions.changeExecutionStarted
    );
  }

  executionFeedbackChanged(value: string): void {
    this.immoTask.executionFeedback = value;
    this.immoIPPlacerImmoTaskChangeStorageHandler.storeOfflineTaskChange(
      value,
      ImmoIPPlacerImmoTaskChangesFunctions.changeExecutionFeedback
    );
  }

  completedToDoItemChanged(): void {
    ImmoIPPlacerImmoTaskDetailHelper.handleCompletedToDoItemChanged(
      this.immoTask,
      this.user,
      this.immoIPPlacerImmoTaskChangeStorageHandler
    );
  }

  offlineAssetsAdded(assets: Asset[]): void {
    ImmoIPPlacerImmoTaskDetailHelper.handleOfflineAssetsAdded(
      this.immoTask,
      assets,
      this.immoIPImmoTaskSubmitQueueService,
      this.immoIPPlacerImmoTaskChangeStorageHandler
    );
  }

  assetWasDeleted(asset: Asset): void {
    const onlineDeletePromise =
      ImmoIPPlacerImmoTaskDetailHelper.handleAssetDeleted(
        asset,
        this.immoIPPlacerImmoTaskChangeStorageHandler
      );
    if (onlineDeletePromise == null) {
      this.snackService.showSuccess('Bestand succesvol verwijderd');
    }
  }

  submitExecuted(executed: boolean): void {
    const submitPromise = ImmoIPPlacerImmoTaskDetailHelper.handleSubmitExecuted(
      this.immoTask,
      executed,
      this.immoIPPlacerImmoTaskChangeStorageHandler
    );
    this.returnToList(submitPromise);
  }

  resetNotExecuted(): void {
    ImmoIPPlacerImmoTaskDetailHelper.handleResetNotExecuted(
      this.immoTask,
      this.immoIPImmoTaskSubmitQueueService,
      this.immoIPPlacerImmoTaskChangeStorageHandler
    ).then((resetNotExecutedSubmitted) => {
      if (!resetNotExecutedSubmitted) {
        this.snackService.showSuccess('Niet uitgevoerd succesvol gereset');
      }
    });
  }

  submitChanges(): void {
    ImmoIPPlacerImmoTaskDetailHelper.handleSubmitChanges(
      this.immoTask,
      this.immoIPImmoTaskSubmitQueueService,
      this.immoIPPlacerImmoTaskChangeStorageHandler
    ).then((fullSubmitQueued) => {
      if (fullSubmitQueued) {
        this.snackService.showSuccess('Feedback succesvol aangepast');
      }
    });
  }

  private returnToList(submitPromise: Promise<void>): void {
    submitPromise
      .then(async () => {
        const tour = await this.offlineStorageTourService.getTour(
          DateHelper.formatDate(this.date, 'yyyyMMdd')
        );
        const allExecuted = tour.tourItems.every((item) =>
          ImmoIPPlacerTourViewHelper.isExecutedOrDelayed(item)
        );
        if (allExecuted) {
          this.modalService.openComponentInModal(
            CompletedAssignmentModalComponent,
            [
              {
                key: 'installer',
                value: this.user,
              },
            ],
            new ObservableHandlerPair(
              (modalRef) => modalRef.afterClosed(),
              () => {}
            ),
            { width: '450px', maxHeight: '300px' }
          );
        }
        this.doReturnToList();
      })
      .catch((err) => {
        if (ErrorHelper.isConnectionLostError(err)) {
          this.doReturnToList();
        }
      });
  }

  private doReturnToList() {
    this.router.navigateByUrl('/ip-installateur');
  }
}
